import React, { useEffect, useMemo, useState } from "react";

import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";

import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CloseIcon from "@mui/icons-material/Close";

const ALL_OPTION = {
  label: "Select All",
  value: "__all__",
};

const SelectedItemsModal = ({
  open,
  onClose,
  title,
  selectedItems,
  onDelete,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {title}

        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          {selectedItems.map((item) => (
            <Chip
              key={item.value}
              label={item.label}
              onDelete={() => onDelete(item)}
            />
          ))}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default function FilterMultiSelect({
  label,
  options,
  value,
  onChange,
}) {
  const [searchText, setSearchText] =
    useState("");

  const [openModal, setOpenModal] =
    useState(false);

  const [isFocused, setIsFocused] = useState(false);

  // SEARCHED OPTIONS
  const filteredOptions = useMemo(() => {
    return [...options]
      .sort((a, b) =>
        a.label.localeCompare(b.label, undefined, {
          sensitivity: "base",
        })
      )
      .filter((option) =>
        option.label
          .toLowerCase()
          .includes(searchText.toLowerCase())
      );
  }, [options, searchText]);

  // REMOVE CHIP
  const handleDelete = (deletedItem) => {
    const updatedValues = value.filter(
      (item) =>
        item.value !== deletedItem.value
    );

    onChange(updatedValues);
  };

  const displayOptions = useMemo(() => {
    const optionMap = new Map();

    optionMap.set(ALL_OPTION.value, ALL_OPTION);

    filteredOptions.forEach((option) => {
      optionMap.set(option.value, option);
    });

    value.forEach((option) => {
      optionMap.set(option.value, option);
    });

    return [...optionMap.values()];
  }, [filteredOptions, value]);

  // HANDLE SELECT
  const handleChange = (
    event,
    newValue,
    reason,
    details
  ) => {
    const clickedOption = details?.option;

    // SELECT ALL
    if (clickedOption?.value === "__all__") {
      let updatedValues = [...value];

      const searchedOptions =
        filteredOptions;

      const allSearchedSelected =
        searchedOptions.length > 0 &&
        searchedOptions.every((option) =>
          updatedValues.some(
            (selected) =>
              selected.value === option.value
          )
        );

      // REMOVE SEARCHED ITEMS
      if (allSearchedSelected) {
        updatedValues = updatedValues.filter(
          (selected) =>
            !searchedOptions.some(
              (searched) =>
                searched.value === selected.value
            )
        );
      }

      // ADD SEARCHED ITEMS
      else {
        searchedOptions.forEach((option) => {
          const exists = updatedValues.some(
            (selected) =>
              selected.value === option.value
          );

          if (!exists) {
            updatedValues.push(option);
          }
        });
      }

      onChange(updatedValues);

      return;
    }

    // NORMAL SELECT
    const cleanedValues = newValue.filter(
      (item) => item.value !== "__all__"
    );

    onChange(cleanedValues);
  };

  const handlePasteFromClipboard = async () => {
    try {
      const clipboardText = await navigator.clipboard.readText();

      if (!clipboardText) return;

      const pastedValues = clipboardText
        .split(/[\n,]/)
        .map(item => item.trim())
        .filter(Boolean);

      const normalize = (str = "") =>
        str
          .toString()
          .toLowerCase()
          .replace(/\s+/g, " ")
          .trim();

      const matchedOptions = options.filter(option => {
        const optionValue = normalize(option.value);
        const optionLabel = normalize(option.label);

        const parts = option.label.split("|");

        const codePart = normalize(parts[0] || "");
        const descPart = normalize(parts.slice(1).join("|") || "");

        return pastedValues.some(pasted => {
          const searchValue = normalize(pasted);

          return (
            searchValue === optionValue ||
            searchValue === optionLabel ||
            searchValue === codePart ||
            searchValue === descPart
          );
        });
      });

      // Nothing matched -> keep existing selections
      if (matchedOptions.length === 0) {
        return;
      }

      // At least one matched -> replace existing selections
      onChange(matchedOptions);
    } catch (error) {
      console.error("Clipboard access failed", error);
    }
  };

  return (
    <>
      <Autocomplete
        multiple
        disableCloseOnSelect
        options={displayOptions}
        filterOptions={(x) => x}
        popupIcon={<KeyboardArrowDownIcon />}
        value={value}
        onChange={handleChange}
        inputValue={searchText}
        onInputChange={(e, value) =>
          setSearchText(value)
        }
        getOptionLabel={(option) =>
          option.label
        }
        isOptionEqualToValue={(option, val) =>
          option.value === val.value
        }
        renderOption={(props, option) => {
          const isSelectAll =
            option.value === "__all__";

          const allSelected =
            filteredOptions.length > 0 &&
            filteredOptions.every((option) =>
              value.some(
                (selected) =>
                  selected.value === option.value
              )
            );

          const checked = isSelectAll
            ? allSelected
            : value.some(
              (selected) =>
                selected.value === option.value
            );

          return (
            <li {...props} key={`${option.value}_${option.label}`} title={option.label}>
              <Checkbox checked={checked} />

              {option.label}
            </li>
          );
        }}
        renderTags={(selectedValues) => {
          if (!selectedValues || selectedValues.length === 0) {
            return null;
          }

          // WHEN INPUT IS FOCUSED -> SHOW ALL CHIPS
          if (isFocused) {
            return selectedValues.map((option) => (
              <Chip
                key={`${option.value}_${option.label}`}
                label={option.label}
                title={option.label}
                size="small"
                onDelete={() => handleDelete(option)}
                sx={{
                  maxWidth: "150px",
                  margin: 0.1
                }}
              />
            ));
          }

          // WHEN BLURRED -> SHOW ONLY 2 CHIPS
          return (
            <>
              {selectedValues.slice(0, 2).map((option) => (
                <Chip
                  key={`${option.value}_${option.label}`}
                  label={option.label}
                  title={option.label}
                  size="small"
                  onDelete={() => handleDelete(option)}
                  sx={{
                    maxWidth: "150px",
                    margin: 0.1
                  }}
                />
              ))}

              {selectedValues.length > 2 && (
                <Typography
                  sx={{
                    fontSize: "14px",
                    cursor: "pointer",
                    color: "#000",
                    fontWeight: 500,
                    alignSelf: "center",
                    ml: 0.5,
                  }}
                  onClick={() => setOpenModal(true)}
                >
                  +{selectedValues.length - 2}
                </Typography>
              )}
            </>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder="Search"
            size="small"
            style={{ fontSize: "11px" }}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
          />
        )}
        sx={{
          "& .MuiOutlinedInput-root": {
            minHeight: "32px",
            background: "#fff",
            alignItems: "flex-start",
            pt: 0.5,
          },

          "& .MuiAutocomplete-tag": {
            margin: "2px",
          },
        }}
      />

      <Typography
        sx={{
          fontSize: "9px",
          color: "#5c6b8a",
          textAlign: "right",
          mt: 0.5,
          mr: 1,
          cursor: "pointer",
        }}
        onClick={handlePasteFromClipboard}
      >
        PASTE FROM CLIPBOARD
      </Typography>

      {/* SELECTED ITEMS MODAL */}

      <SelectedItemsModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        title={label}
        selectedItems={value}
        onDelete={handleDelete}
      />
    </>
  );
}